import styled, { css, keyframes } from 'styled-components'

export interface CardStyleProps {
    variant?: 'default' | 'elevated' | 'outlined'
    padding?: 'none' | 'small' | 'medium' | 'large'
    fullWidth?: boolean
}

const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(8px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`

const getVariantStyles = (variant: CardStyleProps['variant'] = 'default') => {
    switch (variant) {
        case 'default':
            return css`
                background-color: ${({ theme }) => theme.colors.background.bg1};
            `
        case 'elevated':
            return css`
                background-color: ${({ theme }) => theme.colors.background.bg2};
                border: none;
                box-shadow: ${({ theme }) => theme.shadows.lg};
            `
        case 'outlined':
            return css`
                background-color: ${({ theme }) => theme.colors.background.bg2};
                border: 1px solid
                    ${({ theme }) => theme.colors.border.secondary};
                box-shadow: ${({ theme }) => theme.shadows.none};
            `
        default:
            return css``
    }
}

const getPaddingStyles = (padding: CardStyleProps['padding'] = 'medium') => {
    switch (padding) {
        case 'none':
            return css`
                padding: 0;
            `
        case 'small':
            return css`
                padding: ${({ theme }) => theme.spacing[3]};
            `
        case 'medium':
            return css`
                padding: ${({ theme }) => theme.spacing[4]};
            `
        case 'large':
            return css`
                padding: ${({ theme }) => theme.spacing[6]};
            `
        default:
            return css``
    }
}

export const StyledCard = styled.div.withConfig({
    shouldForwardProp: (prop) =>
        !['variant', 'padding', 'fullWidth'].includes(prop),
})<CardStyleProps>`
    width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
    border-radius: ${({ theme }) => theme.borderRadius.lg};
    overflow: hidden;
    color: ${({ theme }) => theme.colors.text.primary};
    height: 100%;
    box-sizing: border-box;

    /* Animation styles */
    opacity: 0;
    transform: translateY(8px);
    animation: ${fadeIn} 0.4s ease-in-out forwards;
    animation-fill-mode: backwards;

    ${({ variant }) => getVariantStyles(variant)}
    ${({ padding }) => getPaddingStyles(padding)}
    display: flex;
    flex-direction: column;
`
